import { api } from './client';
import { Note, NotePickerOption } from '../types/note';

interface NotesResponse {
  status: string;
  data: {
    notes: Note[];
    meta: {
      total: number;
      currentPage: number;
      currentPageTotal: number;
      lastPage: number;
      perPage: number;
    };
  };
}

interface NoteResponse {
  status: string;
  data: {
    note: Note;
  };
}

interface NoteSuccessResponse {
  status: string;
}

interface ExportResponse {
  status: string;
  data: {
    url: string;
  };
}

interface EmailResponse {
  status: string;
}

export const getNotes = async (params?: {
  ignore_note?: string;
  title_search_term?: string;
  filters?: string;
  sort?: string;
  page?: number;
  per_page?: number;
  picker?: boolean;
  tag?: string;
}) => {
  let url = '/notes';

  let filters = null;
  let sorts = null;
  const picker = params?.picker === true;
  const titleSearch = params?.title_search_term;
  const ignoreNote = params?.ignore_note;

  if (params?.filters) {
    filters = `${params.filters}`;
    delete params.filters;
  }
  if (params?.sort) {
    sorts = `${params.sort}`;
    delete params.sort;
  }

  const page = params?.page;
  const perPage = params?.per_page;
  if (params?.page !== undefined) {
    delete params.page;
  }
  if (params?.per_page !== undefined) {
    delete params.per_page;
  }
  if (params?.picker !== undefined) {
    delete params.picker;
  }
  if (params?.title_search_term !== undefined) {
    delete params.title_search_term;
  }
  if (params?.ignore_note !== undefined) {
    delete params.ignore_note;
  }

  const urlParams = [filters, sorts]
    .filter((item) => item !== null)
    .concat(
      [
        picker ? 'picker=1' : null,
        ignoreNote != null && ignoreNote !== ''
            ? `ignore_note=${encodeURIComponent(String(ignoreNote))}`
            : null,
        titleSearch != null && titleSearch !== ''
            ? `title_search_term=${encodeURIComponent(titleSearch)}`
            : null,
        page != null ? `page=${encodeURIComponent(String(page))}` : null,
        perPage != null ? `per_page=${encodeURIComponent(String(perPage))}` : null,
      ].filter((item) => item !== null) as string[],
    )
    .join('&');

  if (urlParams) {
    url += `?${urlParams}`;
  }

  const response = await api.get<NotesResponse>(url, { params });
  return response.data.data;
};

const RELATED_NOTES_PICKER_PER_PAGE = 50;

/** Related-notes autocomplete: id + name only, fetched on demand. */
export const getNotesPicker = async (params?: {
  ignore_note?: string;
  title_search_term?: string;
  page?: number;
  per_page?: number;
}) =>
  getNotes({
    ...params,
    picker: true,
    per_page: params?.per_page ?? RELATED_NOTES_PICKER_PER_PAGE,
    page: params?.page ?? 1,
  }).then((data) => ({
    ...data,
    notes: data.notes as NotePickerOption[],
  }));

export const getNote = async (id: string) => {
  const response = await api.get<NoteResponse>(`/notes/${id}`);
  return response.data.data.note;
};

/** Write payload: `tags` are tag names (or ids). Omit `tags` to keep the note's tags unchanged. */
export type NoteWritePayload = Partial<Omit<Note, 'tags'>> & { tags?: string[] };

export const createNote = async (data: NoteWritePayload): Promise<Note> => {
  const response = await api.post<NoteResponse>('/notes', {
    name: data.name,
    status: data.status ?? '',
    description: data.description,
    related_note_ids: data.related_note_ids || [],
    notify_about_changes: data.notify_about_changes,
    reminder_at: data.reminder_at ?? null,
    ...(data.tags !== undefined ? { tags: data.tags } : {}),
  });
  return response.data.data.note;
};

export const updateNote = async (id: string, data: NoteWritePayload) => {
  const response = await api.put<NoteSuccessResponse>(`/notes/${id}`, {
    name: data.name,
    status: data.status ?? '',
    description: data.description,
    related_note_ids: data.related_note_ids || [],
    notify_about_changes: data.notify_about_changes,
    reminder_at: data.reminder_at ?? null,
    ...(data.tags !== undefined ? { tags: data.tags } : {}),
  });
  return response.data;
};

export const deleteNote = async (id: string) => {
  await api.delete(`/notes/${id}`);
};

export const exportNote = async (id: string, type: 'txt' | 'html' | 'pdf') => {
  const response = await api.get<ExportResponse>(`/notes/${id}/export`, {
    params: { type }
  });
  return response.data.data.url;
};

export const sendNoteByEmail = async (id: string, email: string, subject: string) => {
  const response = await api.post<EmailResponse>(`/notes/${id}/email`, {
    email,
    subject
  });
  return response.data;
};

export const getMindMapNotes = async () => {
  const response = await api.get<{data: {notes: Array<{id: string, name: string, related_notes: string[]}>}}>('/mind-map-notes');
  return response.data.data;
};
